import { useEffect, useRef, useState } from 'react'
import { ApolloCache, FetchResult, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import OrgCreateDetail, { EditOrgHandler } from './org-create-child-detail';
import { CREATE_ORG, GET_CHILD_ORGS, GET_ORG_CATEGORY, GET_ORG_RELATIONSHIPS, GET_ROOT_ORGS } from '../../../graphql/org-gql';
import { ICategory, IOrg, IOrgResponse, isCategory } from '../../../types/org';
import { CategoryType } from '../../../types/constants';
import { notifySuccess } from '../../../services/notification';
import { AddIcon, CancelIcon } from '../../common/icons';
import '../org.scss';
import SpinnerOverlay from '../../common/spinner/spinner';
import Button from '../../common/button';

type OrgCreateChildProps = {
    parent: ICategory | IOrg,
    isOpen: boolean,
    toggle: () => void
}

const OrgCreateChildModal = (props: OrgCreateChildProps) => {
    const { parent, isOpen, toggle} = props

    const [isAddressMandatory, setIsAddressMandatory]=useState(true);
    const [isCreatable,setIsCreatable]=useState(true);

    const detailRef = useRef<EditOrgHandler>(null)
    const [createOrg, {loading}] = useMutation(CREATE_ORG)

    const onCreateOrg = () => {
        if (detailRef.current && !detailRef.current.validate(true)) {
            return
        }
        let editedOrg = detailRef.current && detailRef.current.getEditedOrg()

        createOrg({
            variables: {
                id: parent?.id,
                input: {
                    ...editedOrg
                },
                isParentCategory: isCategory(parent)
            },
            refetchQueries: getRefetchQuery(),
            awaitRefetchQueries: true,
            update: (cache: ApolloCache<IOrg>, mutationResult: FetchResult) => {
                if (!isCategory(parent) && !parent?.hasChild) {
                    cache.modify({
                        id: `Org:${parent?.id}`,
                        fields: {
                            hasChild() {
                                return true;
                            }
                        }
                    })
                }
            },
            onError: (error) => {
                detailRef.current!.setError(error);
            },
            onCompleted: (data) => {
                toggle()
                data && notifySuccess()
            }
        })
    }

    const getRefetchQuery = () => {
        let query:any = []
        if (isCategory(parent)) {
            query= [...query,{query: GET_ROOT_ORGS,variables: {categoryId: parent?.id}}]
        } else {

            query = [...query,{query: GET_CHILD_ORGS,variables: {id: parent?.id}},
                {query: GET_ORG_RELATIONSHIPS, variables: { orgId: parent.id }}]

            if(parent.sharedToOrgIds){
                const orgIdList = parent.sharedToOrgIds.split(',').map(item => item.trim());
                orgIdList.forEach(id=>query = [...query,{query:GET_CHILD_ORGS,variables:{id}}])
            }
        }
        return query;
            
    }

    const [getOrgCategory,{ data: orgData }] = useLazyQuery<IOrgResponse>(GET_ORG_CATEGORY);

    useEffect(()=>{
        if (parent) {
            if (!isCategory(parent)) {
                getOrgCategory({variables:{ orgId:parent.id}});
            } else {
                setIsAddressMandatory(parent.type === CategoryType.ORGANIZATION)
            }
        }
    },[parent])

    useEffect(()=>{
        orgData &&          
            setIsAddressMandatory(orgData.org?.details?.category?.type === CategoryType.ORGANIZATION)     
    },[orgData])

    const onChange = () => {
        if(detailRef.current && detailRef.current.validate(true)) {
            setIsCreatable(true)
        } else {
            setIsCreatable(false)
        }
    }

    const onToggle = (event: any) => {
        if (event.target.nodeName !== 'SELECT') {
            toggle()
        }
    }

    return (<>
        {loading && <SpinnerOverlay />}
        <Modal size="lg" isOpen={isOpen} toggle={onToggle} scrollable backdrop="static" autoFocus={false}>
            <ModalHeader toggle={toggle} tag={'h2'}>Create Child Organization</ModalHeader>
            <ModalBody>
                <OrgCreateDetail parent={parent} isAddressMandatory={isAddressMandatory} onChange={onChange} ref={detailRef} autoFocus={true}></OrgCreateDetail>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" id="childOrgCreateBtn" onClick={onCreateOrg} disabled={!isCreatable}>
                    <AddIcon/> Create
                </Button>
                <Button color="secondary" id="childOrgCancelBtn" onClick={toggle}>
                    <CancelIcon/> Cancel
                </Button>

            </ModalFooter>
        </Modal>
        </>
    )
}

export default OrgCreateChildModal;
